// Board templates used by the welcome-setup popup (first sign-in) and the
// new-board picker. Three flavours:
//   - blank:     empty, no lists
//   - simple:    a few open panels, one with a header demo
//   - organizer: GTD-style structure (Inbox / To Do / Projects / etc.)
//
// Each template specifies a default board name, a layout mode, a column
// count (used in grid mode), and an array of seed lists. addBoardFromTemplate
// in useData.js consumes this shape and writes both the board doc and its
// lists subcollection in one batch.

const TIPS_CONTENT = `listicity is text first. No images, no formatting menus — just write. A few things worth knowing:

# HEADERS
Start a line with \`#\` for a header, \`##\` for a sub-header. You can use these to navigate the list — tap or click the header band up top (the "≡ headers" bar) to jump between sections.

# BOARDS
Group related lists into boards. Switch boards from the dropdown next to the logo.

# FREE VS GRID
Toggle "free / grid" up top. Free mode: drag lists anywhere. Grid: snap them into clean columns.

# AUTO-SAVE + OFFLINE
Saves every few seconds, and every time you switch lists. Works offline too — changes sync when you're back online.

# TAB TO NAVIGATE
Tab cycles through your lists in the order they appear in the sidebar. No mouse required.

# CLOSE WHEN YOU LIKE
Close any list (× in the header) and it stays tucked in the sidebar — open it again anytime. To remove for good, use the ⋯ menu.

# REVERSE CHRONOLOGICAL
A useful default: put the newest items on top, older ones below. Makes it easy to spot what's most recent at a glance.`;

const HEADER_DEMO = `// Example of what # headers look like

# Header 1
## Sub-header 1.1
## Sub-header 1.2

# Header 2
## Sub-header 2.1`;

export const TEMPLATE_BLANK = {
  id: 'blank',
  // Walter 2026-06-09: `label` is the generic name shown in the add-a-board
  // picker (NewBoardModal) for existing users; `welcomeLabel` is the
  // friendlier first-run name shown only in WelcomeSetup. Testers (Frank)
  // landed on the dense organizer first and didn't know where to start, so
  // new users now land on this blank board ("my first board") — but the
  // "my first board" naming only makes sense at first-run, not when an
  // already-set-up user is adding their 5th board.
  label: 'blank board',
  welcomeLabel: 'my first board',
  defaultBoardName: 'my first board',
  description: 'empty — make it your own',
  // Walter 2026-05-26: default to 3-column grid mode (was 'free').
  // Walter's call: the grid mode UX is just better for the first-time
  // experience — columns visually signal "this is where lists go",
  // there's an obvious structure to fill in, and the explicit
  // remove-column / add-column controls give new users a tactile feel
  // for layout. Free mode is still one toggle away in the header.
  mode: 'grid',
  gridColumnCount: 3,
  lists: [],
};

// Simple template uses GRID mode (responsive, snaps cleanly). Per-column
// counts: [2, 1, 2, 2] — Tips and the header demo stack in col 0, col 1
// is a single tall canvas, cols 2 and 3 each have a two-tile stack.
// Mixed heights give visual rhythm without breaking the grid metaphor.
// Users can flip to free mode any time via the "free / grid" toggle if
// they want full positional control.
export const TEMPLATE_SIMPLE = {
  id: 'simple',
  label: 'simple template',
  defaultBoardName: 'simple',
  description: 'a few open panels to start writing',
  mode: 'grid',
  gridColumnCount: 4,
  // Per-tile `h` values are weights, not pixels — GridCanvas distributes
  // each column's vertical space in proportion to these. Distinct values
  // give organic, varied heights across the layout instead of clean
  // 50/50 splits.
  lists: [
    // Col 0: Tips (taller) + Header demo (shorter) → ~60/40 split
    { title: 'Tips and Tricks', content: TIPS_CONTENT, gridCol: 0, gridRow: 0, h: 360 },
    { title: '',                 content: HEADER_DEMO,  gridCol: 0, gridRow: 1, h: 240 },
    // Col 1: single tall tile, weight doesn't matter (no siblings to ratio against)
    { title: '',                 content: '',           gridCol: 1, gridRow: 0, h: 600 },
    // Col 2: top slightly taller than bottom → ~55/45
    { title: '',                 content: '',           gridCol: 2, gridRow: 0, h: 330 },
    { title: '',                 content: '',           gridCol: 2, gridRow: 1, h: 270 },
    // Col 3: top much taller than bottom → ~65/35
    { title: '',                 content: '',           gridCol: 3, gridRow: 0, h: 390 },
    { title: '',                 content: '',           gridCol: 3, gridRow: 1, h: 210 },
  ],
};

export const TEMPLATE_ORGANIZER = {
  id: 'organizer',
  label: 'organizer board',
  welcomeLabel: 'my organizer board',
  defaultBoardName: 'my organizer board',
  description: 'GTD-style: Inbox, To Do, Projects, Someday/Maybe, Notes, Done',
  mode: 'grid',
  gridColumnCount: 4,
  // The titled structure (Inbox / To Do / Projects / etc.) IS the value
  // of this template. When recreating post-signup we keep both the titles
  // AND the seed content — the inline tips and example items are part of
  // what makes the GTD skeleton useful (a fresh blank board with just
  // the titles loses the focused-view tip, the inbox guidance, etc.).
  // The General Tips list is the one exception; we strip it so users
  // don't see the welcome tips repeated on every new board.
  preserveTitlesOnRecreate: true,
  preserveContentOnRecreate: true,
  lists: [
    {
      title: 'General Tips',
      content: TIPS_CONTENT,
      gridCol: 0, gridRow: 0,
    },
    {
      title: 'To Do',
      content: `Tip — lines starting with \`#\` become headers. You can use them to navigate the list — tap or click the header band up top (the "≡ headers" bar).

# WED
Do this
Do that

# TUE
Do the other thing`,
      gridCol: 1, gridRow: 0,
    },
    {
      title: 'Inbox',
      content: `Drop new items here if you haven't figured out what to do with them yet.`,
      gridCol: 2, gridRow: 0,
    },
    {
      title: 'Projects',
      content: `# Project 1
## Sub-project 1a
## Sub-project 1b

# Project 2`,
      gridCol: 2, gridRow: 1,
    },
    {
      title: 'Someday & Maybe',
      content: `Maybe learn Spanish
And then Mandarin`,
      gridCol: 2, gridRow: 2,
    },
    {
      title: 'Notes',
      content: `Things that aren't to-dos but you want to keep. If you take a lot of notes, add more lists here or start a separate board for notes.`,
      gridCol: 3, gridRow: 0,
    },
    {
      title: 'Done',
      content: `Drop things here that are done. It's satisfying to keep a list of completed items — and sometimes useful. You can highlight items in another list and drag them here.`,
      gridCol: 3, gridRow: 1,
    },
    {
      // Designated reminders list — see App.js board-create logic, which
      // looks for the isDesignatedReminderList flag and sets
      // userPrefs.designatedReminderListIds accordingly.
      // Walter 2026-06-16: seeded EMPTY now. The how-it-works examples render
      // as the list's empty-state PLACEHOLDER (getListEmptyHint), not as
      // content — so the instruction lines can never be captured as reminders
      // once the user edits the list. (Previously they were seeded as content
      // and got captured as undated reminders after the first edit.)
      title: 'Reminders',
      content: '',
      gridCol: 1, gridRow: 1,
      isDesignatedReminderList: true,
    },
    {
      // Designated log list — Walter 2026-05-26. Same pattern as the
      // Reminders list above: `isDesignatedLogList: true` is consumed
      // by App.js's welcome flow, which promotes the created list's
      // id into userPrefs.designatedLogListIds. Every new line in a
      // designated log list auto-prepends `@today` on Enter; the LogPanel
      // surface aggregates these across all lists for a time-ordered
      // view. The seed mirrors the Reminders list's style — explain the
      // mechanic, show example phrasings inline, no leading @-tokens so
      // we don't trip the auto-stamper on first render.
      // Walter 2026-06-16: seeded EMPTY — examples are the empty-state
      // placeholder (getListEmptyHint), not content, so log instruction
      // lines can't be auto-@date-stamped after the first edit.
      title: 'Log',
      content: '',
      gridCol: 3, gridRow: 2,
      isDesignatedLogList: true,
    },
  ],
};

// Display order for pickers — most-structure-first reads top-to-bottom for
// users skimming. We surface the welcome-setup popup with this same order.
// Walter 2026-05-11: dropped TEMPLATE_SIMPLE from the welcome picker —
// new users found three options one too many, and the simple template
// was mostly a softer-blank that didn't add much over the actual blank
// board. The definition itself stays exported below in case some code
// path still references it, but the welcome flow now shows only
// organizer + blank.
// Walter 2026-06-09: blank ("my first board") listed FIRST and is the
// landing board — new users start on an empty, unintimidating board.
// The organizer is the second, optional (but pre-checked) example.
export const TEMPLATES = [
  TEMPLATE_BLANK,
  TEMPLATE_ORGANIZER,
];

export function getTemplateById(id) {
  return TEMPLATES.find(t => t.id === id) || TEMPLATE_BLANK;
}

/**
 * stripTemplateContent — returns a copy of a template ready for use as a
 * "new board after signup" seed.
 *
 * Two flags govern what survives:
 *   - preserveTitlesOnRecreate  — keep list titles. Organizer needs this
 *                                 (GTD skeleton: Inbox / To Do / etc.).
 *   - preserveContentOnRecreate — keep list contents. Organizer also
 *                                 needs this so the inline tips and the
 *                                 example projects survive on new boards.
 *
 * Walter 2026-05-26: previously the General Tips list was hardcoded-
 * dropped on every recreate ("we don't want repeated on every new
 * board"). That rationale predated the demo-content band shipped same
 * day, which gives every seeded list a one-click "keep but close"
 * affordance — General Tips can now be quickly tucked into the sidebar
 * (state: 'closed') from the band itself. Showing the welcome
 * walkthrough on every new organizer board is helpful (existing users
 * often forget keyboard shortcuts and the focused-view tip), and
 * trivially dismissible. So the filter is gone.
 *
 * The Simple template (no flags) still gets fully stripped — its
 * "Tips and Tricks" heading on a fresh blank board reads as leftover.
 */
export function stripTemplateContent(template) {
  if (!template || !Array.isArray(template.lists)) return template;
  const keepTitles = !!template.preserveTitlesOnRecreate;
  const keepContent = !!template.preserveContentOnRecreate;
  // Strip titles/content per flags; General Tips no longer specially
  // dropped (see header comment above).
  const lists = template.lists
    .map(l => ({
      ...l,
      title: keepTitles ? l.title : '',
      content: keepContent ? l.content : '',
    }));

  // Step 2: dropping the welcome-tips list (which sat at gridCol 0)
  // leaves an empty leftmost column. Reflow the remaining lists by
  // shifting them left by the smallest occupied column index, and
  // recompute gridColumnCount so the canvas isn't sized to a column
  // that no longer has any tiles.
  const usedCols = lists
    .map(l => (typeof l.gridCol === 'number' ? l.gridCol : null))
    .filter(c => c != null);
  if (usedCols.length === 0) {
    return { ...template, lists };
  }
  const minCol = Math.min(...usedCols);
  const shifted = minCol > 0
    ? lists.map(l => ({ ...l, gridCol: (l.gridCol ?? minCol) - minCol }))
    : lists;
  const maxCol = Math.max(...shifted.map(l => l.gridCol ?? 0));
  const gridColumnCount = Math.max(1, maxCol + 1);

  return {
    ...template,
    gridColumnCount,
    lists: shifted,
  };
}
